import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Users, Music, Building2, FolderOpen, FileText, 
  MessageSquare, Star, Settings, Activity, Home, Inbox,
  ChevronRight, Bell, LogOut, Check, X, Clock, Eye, Plus, Edit, Trash2,
  Save, ArrowLeft, Shield, UserCog, ToggleLeft, ToggleRight, Search,
  Camera, ExternalLink, AlertCircle, Truck, RefreshCw, Send, Mail, Upload,
  Image, Video, GripVertical, Megaphone, ChevronDown, Calendar, CreditCard, QrCode, BarChart3
} from 'lucide-react';
import TransportTab from '../../components/TransportTab';
import EventsTab from './EventsTab';
import PaymentsTab from './PaymentsTab';
import CustomersTab from './CustomersTab';
import CheckInTab from './CheckInTab';
import InvitationsTab from './InvitationsTab';
import AnalyticsTab from './AnalyticsTab';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { AdminInbox } from '../../components/Inbox';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    draft: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Borrador' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pendiente' },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Aprobado' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Rechazado' },
    changes_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Cambios' },
    active: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Activo' },
    inactive: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Inactivo' },
    new: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Nuevo' },
    contacted: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Contactado' },
    in_progress: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'En Proceso' },
    completed: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Cerrado' },
    hidden: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Oculto' },
    archived: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Archivado' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Featured Toggle Component
const FeaturedToggle = ({ featured, onToggle, loading }) => (
  <button
    onClick={onToggle}
    disabled={loading}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
      featured 
        ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30' 
        : 'bg-white/5 text-white/40 border border-white/10 hover:border-white/20'
    }`}
  >
    {featured ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
    {featured ? 'Destacado' : 'No destacado'}
  </button>
);

// Media Gallery Component for editing
const MediaGalleryEditor = ({ images, videos, onChange, token }) => {
  const [uploading, setUploading] = useState(false);
  
  const handleFileUpload = async (e, type = 'image') => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newUrls = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload?folder=content`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        newUrls.push(fullUrl);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    
    if (type === 'image') {
      onChange({ images: [...images, ...newUrls], videos });
    } else {
      onChange({ images, videos: [...videos, ...newUrls] });
    }
    
    setUploading(false);
    toast.success(`${newUrls.length} archivo(s) subido(s)`);
  };
  
  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange({ images: newImages, videos });
  };
  
  const removeVideo = (index) => {
    const newVideos = videos.filter((_, i) => i !== index);
    onChange({ images, videos: newVideos });
  };
  
  return (
    <div className="space-y-4">
      {/* Images Section */}
      <div>
        <label className="block text-white/70 text-sm mb-2">Galería de Imágenes</label>
        <div className="grid grid-cols-4 gap-3 mb-3">
          {images.map((img, idx) => (
            <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden bg-white/5">
              <img src={img} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage(idx)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={14} />
              </button>
              <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-xs">
                {idx + 1}
              </div>
            </div>
          ))}
          <label className="aspect-square rounded-lg border-2 border-dashed border-white/20 hover:border-white/40 flex flex-col items-center justify-center cursor-pointer transition-colors">
            <Upload size={24} className="text-white/40 mb-2" />
            <span className="text-white/40 text-xs">Agregar</span>
            <input 
              type="file" 
              accept="image/*" 
              multiple 
              className="hidden" 
              onChange={(e) => handleFileUpload(e, 'image')}
              disabled={uploading}
            />
          </label>
        </div>
      </div>
      
      {/* Videos Section */}
      <div>
        <label className="block text-white/70 text-sm mb-2">Videos</label>
        <div className="space-y-2 mb-3">
          {videos.map((vid, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <Video size={18} className="text-white/40" />
              <span className="flex-1 text-white/70 text-sm truncate">{vid}</span>
              <button
                onClick={() => removeVideo(idx)}
                className="p-1.5 rounded-full hover:bg-red-500/20 text-red-400 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 hover:border-white/40 cursor-pointer transition-colors">
          <Video size={18} className="text-white/40" />
          <span className="text-white/60 text-sm">Agregar Video</span>
          <input 
            type="file" 
            accept="video/*" 
            className="hidden" 
            onChange={(e) => handleFileUpload(e, 'video')}
            disabled={uploading}
          />
        </label>
      </div>
      
      {uploading && (
        <div className="flex items-center gap-2 text-[#D4AF37]">
          <RefreshCw size={16} className="animate-spin" />
          <span className="text-sm">Subiendo archivos...</span>
        </div>
      )}
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, userRole }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Panel General' },
    { id: 'homepage', icon: Home, label: 'Control Homepage' },
    { id: 'pending', icon: Clock, label: 'Revisiones Pendientes' },
    { id: 'projects', icon: FolderOpen, label: 'Proyectos', highlight: true },
    { id: 'artists', icon: Music, label: 'Artistas' },
    { id: 'venues', icon: Building2, label: 'Venues' },
    { id: 'models', icon: Camera, label: 'Modelos' },
    { id: 'transport', icon: Truck, label: 'Transporte' },
    { id: 'events', icon: Calendar, label: 'Eventos' },
    { id: 'payments', icon: CreditCard, label: 'Pagos' },
    { id: 'customers', icon: Users, label: 'Clientes' },
    { id: 'checkin', icon: QrCode, label: 'Check-in' },
    { id: 'invitations', icon: Mail, label: 'Invitaciones' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
    { id: 'leads', icon: MessageSquare, label: 'Leads' },
    { id: 'users', icon: UserCog, label: 'Gestión Usuarios' },
    { id: 'divider1', divider: true },
    { id: 'inbox', icon: MessageSquare, label: 'Inbox / Chat', highlight: true },
    { id: 'messages', icon: Send, label: 'Anuncios Masivos', highlight: true },
    { id: 'emails', icon: Mail, label: 'Enviar Emails', highlight: true },
    { id: 'divider2', divider: true },
    { id: 'settings', icon: Settings, label: 'Configuración' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col z-40">
      <div className="p-6 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2">
          <img src="/gs-agency-logo-gold.png" alt="GS Agency" className="h-12 w-auto object-contain" />
        </Link>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <p className="text-[#D4AF37] text-xs uppercase tracking-wider px-4 mb-4 flex items-center gap-2">
          <Shield size={12} /> Super Admin
        </p>
        {menuItems.map((item) => {
          if (item.divider) {
            return <div key={item.id} className="my-3 border-t border-white/5" />;
          }
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all mb-1 ${
                activeTab === item.id
                  ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                  : item.highlight
                  ? 'text-white/80 hover:text-white hover:bg-white/5'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5">
          <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
            <Shield size={18} className="text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-white/40 text-xs">Super Admin</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 mt-2 text-sm transition-colors"
        >
          <LogOut size={18} />
          Cerrar Sesión
        </button>
      </div>
    </aside>
  );
};

// Overview Tab
const OverviewTab = ({ stats, loading, onNavigate }) => {
  const statCards = [
    { label: 'Artistas', value: stats?.artists?.total || 0, icon: Music, color: '#D4AF37', tab: 'artists' },
    { label: 'Venues', value: stats?.venues?.total || 0, icon: Building2, color: '#C0C0C0', tab: 'venues' },
    { label: 'Proyectos', value: stats?.projects?.total || 0, icon: FolderOpen, color: '#D4AF37', tab: 'projects' },
    { label: 'Leads', value: stats?.leads?.total || 0, icon: MessageSquare, color: '#10B981', tab: 'leads' },
    { label: 'Modelos', value: stats?.models?.total || 0, icon: Camera, color: '#EC4899', tab: 'models' },
    { label: 'Transporte', value: stats?.transport?.total || 0, icon: Truck, color: '#8B5CF6', tab: 'transport' },
    { label: 'Pendientes', value: (stats?.artists?.pending || 0) + (stats?.venues?.pending || 0) + (stats?.projects?.pending || 0) + (stats?.models?.pending || 0), icon: Clock, color: '#F59E0B', tab: 'pending' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl text-white mb-2">Panel de Control</h1>
        <p className="text-white/50">Vista general del sistema</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, idx) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            onClick={() => onNavigate(stat.tab)}
            className="glass-dark rounded-xl p-5 border border-white/5 cursor-pointer hover:border-white/20 transition-colors"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}15` }}>
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
            </div>
            <p className="text-3xl font-display text-white mb-1">{loading ? '...' : stat.value}</p>
            <p className="text-white/50 text-sm">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="glass-dark rounded-xl p-6 border border-white/5">
        <h2 className="text-white font-display text-xl mb-4">Acciones Rápidas</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button onClick={() => onNavigate('pending')} className="p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left">
            <Clock className="text-yellow-400 mb-2" size={24} />
            <p className="text-white text-sm font-medium">Revisar Pendientes</p>
            <p className="text-white/40 text-xs">{stats?.pending_reviews || 0} items</p>
          </button>
          <button onClick={() => onNavigate('projects')} className="p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left">
            <Plus className="text-green-400 mb-2" size={24} />
            <p className="text-white text-sm font-medium">Nuevo Proyecto</p>
            <p className="text-white/40 text-xs">Crear y publicar</p>
          </button>
          <button onClick={() => onNavigate('messages')} className="p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left">
            <Send className="text-blue-400 mb-2" size={24} />
            <p className="text-white text-sm font-medium">Enviar Mensaje</p>
            <p className="text-white/40 text-xs">Notificación interna</p>
          </button>
          <button onClick={() => onNavigate('emails')} className="p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left">
            <Mail className="text-purple-400 mb-2" size={24} />
            <p className="text-white text-sm font-medium">Enviar Email</p>
            <p className="text-white/40 text-xs">Anuncio por correo</p>
          </button>
        </div>
      </div>
    </div>
  );
};

// Project Editor Component
const ProjectEditor = ({ project, token, onSave, onCancel, isNew }) => {
  const [form, setForm] = useState({
    title: { es: '', en: '' },
    description: { es: '', en: '' },
    summary: { es: '', en: '' },
    category: '',
    city: '',
    country: '',
    date: '',
    cover_image: '',
    gallery: [],
    videos: [],
    featured: false,
    status: 'draft',
    services_involved: [],
    tags: [],
    related_artists: [],
    related_venues: [],
    ...project
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [availableArtists, setAvailableArtists] = useState([]);
  const [availableVenues, setAvailableVenues] = useState([]);
  
  const categories = ['Evento Privado', 'Festival', 'Corporativo', 'Producción Artística', 'Activación de Marca', 'Concierto', 'Gira'];
  const services = ['Booking Artístico', 'Management', 'Producción de Eventos', 'Producción Audiovisual', 'Branding & Marketing', 'Experiencias Exclusivas'];
  
  // Fetch available artists and venues for relationships
  useEffect(() => {
    const fetchRelationData = async () => {
      try {
        const [artistsRes, venuesRes] = await Promise.all([
          axios.get(`${API}/admin/artists`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/venues`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setAvailableArtists(artistsRes.data.items?.filter(a => a.status === 'approved') || []);
        setAvailableVenues(venuesRes.data.items?.filter(v => v.status === 'approved') || []);
      } catch (error) {
        console.error('Error fetching relation data:', error);
      }
    };
    fetchRelationData();
  }, [token]);
  
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload?folder=projects`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setForm(prev => ({ ...prev, cover_image: fullUrl }));
      toast.success('Cover subido');
    } catch (error) {
      toast.error('Error subiendo imagen');
    } finally {
      setUploading(false);
    }
  };
  
  const handleSubmit = async (status = form.status) => {
    setSaving(true);
    try {
      const payload = { ...form, status };
      
      if (isNew) {
        await axios.post(`${API}/admin/projects`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proyecto creado');
      } else {
        await axios.put(`${API}/admin/projects/${project.project_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proyecto actualizado');
      }
      onSave();
    } catch (error) {
      toast.error('Error guardando proyecto');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onCancel} className="p-2 rounded-lg hover:bg-white/10 text-white/60">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-display text-2xl text-white">{isNew ? 'Nuevo Proyecto' : 'Editar Proyecto'}</h1>
            <p className="text-white/50 text-sm">Completa la información del proyecto</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => handleSubmit('draft')} disabled={saving} className="btn-secondary px-6">
            {saving ? 'Guardando...' : 'Guardar Borrador'}
          </button>
          <button onClick={() => handleSubmit('approved')} disabled={saving} className="btn-gold px-6 flex items-center gap-2">
            <Check size={18} /> Publicar
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Cover Image */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <label className="block text-white/70 text-sm mb-3">Imagen de Cover</label>
            {form.cover_image ? (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-white/5">
                <img src={form.cover_image} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => setForm(prev => ({ ...prev, cover_image: '' }))}
                  className="absolute top-3 right-3 p-2 rounded-full bg-red-500 text-white"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="block aspect-video rounded-lg border-2 border-dashed border-white/20 hover:border-white/40 flex flex-col items-center justify-center cursor-pointer transition-colors">
                <Image size={48} className="text-white/20 mb-3" />
                <span className="text-white/40">Click para subir cover</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploading} />
              </label>
            )}
          </div>
          
          {/* Title & Description */}
          <div className="glass-dark rounded-xl p-6 border border-white/5 space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Título (Español)</label>
              <Input
                value={form.title.es}
                onChange={(e) => setForm(prev => ({ ...prev, title: { ...prev.title, es: e.target.value } }))}
                className="input-premium"
                placeholder="Nombre del proyecto..."
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Título (English)</label>
              <Input
                value={form.title.en}
                onChange={(e) => setForm(prev => ({ ...prev, title: { ...prev.title, en: e.target.value } }))}
                className="input-premium"
                placeholder="Project name..."
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Descripción (Español)</label>
              <Textarea
                value={form.description.es}
                onChange={(e) => setForm(prev => ({ ...prev, description: { ...prev.description, es: e.target.value } }))}
                className="input-premium"
                rows={4}
                placeholder="Descripción detallada del proyecto..."
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Descripción (English)</label>
              <Textarea
                value={form.description.en}
                onChange={(e) => setForm(prev => ({ ...prev, description: { ...prev.description, en: e.target.value } }))}
                className="input-premium"
                rows={4}
                placeholder="Detailed project description..."
              />
            </div>
          </div>
          
          {/* Gallery */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <MediaGalleryEditor
              images={form.gallery}
              videos={form.videos}
              onChange={({ images, videos }) => setForm(prev => ({ ...prev, gallery: images, videos }))}
              token={token}
            />
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <label className="block text-white/70 text-sm mb-3">Estado</label>
            <select
              value={form.status}
              onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
              className="input-premium w-full"
            >
              <option value="draft">Borrador</option>
              <option value="pending">Pendiente</option>
              <option value="approved">Publicado</option>
              <option value="hidden">Oculto</option>
              <option value="archived">Archivado</option>
            </select>
            
            <div className="mt-4 pt-4 border-t border-white/10">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm(prev => ({ ...prev, featured: e.target.checked }))}
                  className="w-5 h-5 rounded bg-white/10 border-white/20"
                />
                <span className="text-white/80">Destacar en Homepage</span>
              </label>
            </div>
          </div>
          
          {/* Metadata */}
          <div className="glass-dark rounded-xl p-6 border border-white/5 space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Categoría</label>
              <select
                value={form.category}
                onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                className="input-premium w-full"
              >
                <option value="">Seleccionar...</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Ciudad</label>
              <Input
                value={form.city}
                onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                className="input-premium"
                placeholder="Ciudad del evento..."
              />
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Fecha</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                className="input-premium"
              />
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Servicios Involucrados</label>
              <div className="space-y-2">
                {services.map(service => (
                  <label key={service} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.services_involved?.includes(service)}
                      onChange={(e) => {
                        const newServices = e.target.checked
                          ? [...(form.services_involved || []), service]
                          : (form.services_involved || []).filter(s => s !== service);
                        setForm(prev => ({ ...prev, services_involved: newServices }));
                      }}
                      className="w-4 h-4 rounded bg-white/10 border-white/20"
                    />
                    <span className="text-white/70 text-sm">{service}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          {/* Related Artists */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <label className="block text-white/70 text-sm mb-3 flex items-center gap-2">
              <Music size={16} className="text-[#D4AF37]" /> Artistas Relacionados
            </label>
            {availableArtists.length === 0 ? (
              <p className="text-white/40 text-sm">No hay artistas aprobados disponibles</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableArtists.map(artist => (
                  <label key={artist.artist_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.related_artists?.includes(artist.artist_id)}
                      onChange={(e) => {
                        const newArtists = e.target.checked
                          ? [...(form.related_artists || []), artist.artist_id]
                          : (form.related_artists || []).filter(id => id !== artist.artist_id);
                        setForm(prev => ({ ...prev, related_artists: newArtists }));
                      }}
                      className="w-4 h-4 rounded bg-white/10 border-white/20"
                    />
                    <div className="flex items-center gap-2 flex-1">
                      {artist.cover_image && (
                        <img src={artist.cover_image} alt="" className="w-8 h-8 rounded-full object-cover" />
                      )}
                      <span className="text-white/80 text-sm">{artist.name?.es || artist.name?.en}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {form.related_artists?.length > 0 && (
              <p className="text-[#D4AF37] text-xs mt-2">{form.related_artists.length} artista(s) seleccionado(s)</p>
            )}
          </div>
          
          {/* Related Venues */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <label className="block text-white/70 text-sm mb-3 flex items-center gap-2">
              <Building2 size={16} className="text-[#C0C0C0]" /> Venues Relacionados
            </label>
            {availableVenues.length === 0 ? (
              <p className="text-white/40 text-sm">No hay venues aprobados disponibles</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableVenues.map(venue => (
                  <label key={venue.venue_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.related_venues?.includes(venue.venue_id)}
                      onChange={(e) => {
                        const newVenues = e.target.checked
                          ? [...(form.related_venues || []), venue.venue_id]
                          : (form.related_venues || []).filter(id => id !== venue.venue_id);
                        setForm(prev => ({ ...prev, related_venues: newVenues }));
                      }}
                      className="w-4 h-4 rounded bg-white/10 border-white/20"
                    />
                    <div className="flex items-center gap-2 flex-1">
                      {venue.cover_image && (
                        <img src={venue.cover_image} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <span className="text-white/80 text-sm">{venue.name?.es || venue.name?.en}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {form.related_venues?.length > 0 && (
              <p className="text-[#C0C0C0] text-xs mt-2">{form.related_venues.length} venue(s) seleccionado(s)</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Content Editor Component (for Artists, Venues, Models)
const ContentEditor = ({ type, item, token, onSave, onCancel }) => {
  const [form, setForm] = useState({ ...item });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const typeConfig = {
    artists: { 
      label: 'Artista', 
      idField: 'artist_id',
      endpoint: 'artists',
      nameField: 'name',
      descField: 'biography'
    },
    venues: { 
      label: 'Venue', 
      idField: 'venue_id',
      endpoint: 'venues',
      nameField: 'name',
      descField: 'description'
    },
    models: { 
      label: 'Modelo', 
      idField: 'model_id',
      endpoint: 'models',
      nameField: 'name',
      descField: 'biography'
    }
  };
  
  const config = typeConfig[type];
  
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload?folder=${type}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setForm(prev => ({ ...prev, cover_image: fullUrl }));
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error subiendo imagen');
    } finally {
      setUploading(false);
    }
  };
  
  const handleSubmit = async (status = form.status) => {
    setSaving(true);
    try {
      const payload = { ...form, status };
      
      await axios.put(`${API}/admin/${config.endpoint}/${item[config.idField]}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${config.label} actualizado`);
      onSave();
    } catch (error) {
      toast.error('Error guardando');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onCancel} className="p-2 rounded-lg hover:bg-white/10 text-white/60">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-display text-2xl text-white">Editar {config.label}</h1>
            <p className="text-white/50 text-sm">{form[config.nameField]?.es || form[config.nameField]?.en}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => handleSubmit('draft')} disabled={saving} className="btn-secondary px-6">
            Guardar Borrador
          </button>
          <button onClick={() => handleSubmit('approved')} disabled={saving} className="btn-gold px-6 flex items-center gap-2">
            <Check size={18} /> Publicar
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Cover Image */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <label className="block text-white/70 text-sm mb-3">Imagen Principal</label>
            {form.cover_image ? (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-white/5">
                <img src={form.cover_image} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => setForm(prev => ({ ...prev, cover_image: '' }))}
                  className="absolute top-3 right-3 p-2 rounded-full bg-red-500 text-white"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="block aspect-video rounded-lg border-2 border-dashed border-white/20 hover:border-white/40 flex flex-col items-center justify-center cursor-pointer transition-colors">
                <Image size={48} className="text-white/20 mb-3" />
                <span className="text-white/40">Click para subir imagen</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploading} />
              </label>
            )}
          </div>
          
          {/* Name & Bio */}
          <div className="glass-dark rounded-xl p-6 border border-white/5 space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Nombre (Español)</label>
              <Input
                value={form[config.nameField]?.es || ''}
                onChange={(e) => setForm(prev => ({ 
                  ...prev, 
                  [config.nameField]: { ...prev[config.nameField], es: e.target.value } 
                }))}
                className="input-premium"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Nombre (English)</label>
              <Input
                value={form[config.nameField]?.en || ''}
                onChange={(e) => setForm(prev => ({ 
                  ...prev, 
                  [config.nameField]: { ...prev[config.nameField], en: e.target.value } 
                }))}
                className="input-premium"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">{type === 'venues' ? 'Descripción' : 'Biografía'} (Español)</label>
              <Textarea
                value={form[config.descField]?.es || ''}
                onChange={(e) => setForm(prev => ({ 
                  ...prev, 
                  [config.descField]: { ...prev[config.descField], es: e.target.value } 
                }))}
                className="input-premium"
                rows={4}
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">{type === 'venues' ? 'Descripción' : 'Biografía'} (English)</label>
              <Textarea
                value={form[config.descField]?.en || ''}
                onChange={(e) => setForm(prev => ({ 
                  ...prev, 
                  [config.descField]: { ...prev[config.descField], en: e.target.value } 
                }))}
                className="input-premium"
                rows={4}
              />
            </div>
          </div>
          
          {/* Gallery */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <MediaGalleryEditor
              images={form.gallery || []}
              videos={form.videos || []}
              onChange={({ images, videos }) => setForm(prev => ({ ...prev, gallery: images, videos }))}
              token={token}
            />
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <label className="block text-white/70 text-sm mb-3">Estado</label>
            <select
              value={form.status}
              onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
              className="input-premium w-full"
            >
              <option value="draft">Borrador</option>
              <option value="pending">Pendiente</option>
              <option value="approved">Publicado</option>
              <option value="rejected">Rechazado</option>
              <option value="changes_requested">Cambios Solicitados</option>
              <option value="hidden">Oculto</option>
              <option value="archived">Archivado</option>
            </select>
            
            <div className="mt-4 pt-4 border-t border-white/10">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm(prev => ({ ...prev, featured: e.target.checked }))}
                  className="w-5 h-5 rounded bg-white/10 border-white/20"
                />
                <span className="text-white/80">Destacar en Homepage</span>
              </label>
            </div>
          </div>
          
          {/* Location */}
          <div className="glass-dark rounded-xl p-6 border border-white/5 space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Ciudad</label>
              <Input
                value={form.city || ''}
                onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                className="input-premium"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">País</label>
              <Input
                value={form.country || ''}
                onChange={(e) => setForm(prev => ({ ...prev, country: e.target.value }))}
                className="input-premium"
              />
            </div>
            {type === 'venues' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Capacidad</label>
                <Input
                  type="number"
                  value={form.capacity || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, capacity: parseInt(e.target.value) || 0 }))}
                  className="input-premium"
                />
              </div>
            )}
          </div>
          
          {/* Review Notes */}
          {form.review_notes?.length > 0 && (
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h4 className="text-white/70 text-sm mb-3">Historial de Revisiones</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {form.review_notes.map((note, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-white/5 text-sm">
                    <p className="text-white/80">{note.notes}</p>
                    <p className="text-white/40 text-xs mt-1">
                      {note.reviewer_name} • {new Date(note.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Content Creator Component (for creating new Artists, Venues, Models)
const ContentCreator = ({ type, config, token, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: { es: '', en: '' },
    biography: { es: '', en: '' },
    description: { es: '', en: '' },
    city: '',
    country: 'México',
    cover_image: '',
    gallery: [],
    videos: [],
    featured: false,
    status: 'draft',
    capacity: 0,
    categories: [],
    venue_type: '',
    genres: []
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const categories = {
    artists: ['DJ', 'Banda', 'Solista', 'Grupo Musical', 'Productor', 'Live Act'],
    venues: ['Club', 'Rooftop', 'Beach Club', 'Hotel', 'Restaurant', 'Centro de Eventos', 'Bar'],
    models: ['Comercial', 'Pasarela', 'Editorial', 'Fitness', 'Promocional', 'Plus Size']
  };
  
  const genres = ['House', 'Techno', 'Pop', 'Rock', 'Jazz', 'Latin', 'Electronic', 'Reggaeton', 'Hip Hop', 'R&B'];
  
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload?folder=${type}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setForm(prev => ({ ...prev, cover_image: fullUrl }));
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error subiendo imagen');
    } finally {
      setUploading(false);
    }
  };
  
  const handleSubmit = async (status = form.status) => {
    if (!form.name.es && !form.name.en) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      const payload = { ...form, status };
      
      await axios.post(`${API}/admin/${type}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${config.singular} creado`);
      onSave();
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Error creando');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onCancel} className="p-2 rounded-lg hover:bg-white/10 text-white/60">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-display text-2xl text-white">Nuevo {config.singular}</h1>
            <p className="text-white/50 text-sm">Completa la información</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => handleSubmit('draft')} disabled={saving} className="btn-secondary px-6">
            {saving ? 'Guardando...' : 'Guardar Borrador'}
          </button>
          <button onClick={() => handleSubmit('approved')} disabled={saving} className="btn-gold px-6 flex items-center gap-2">
            <Check size={18} /> Publicar
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Cover Image */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <label className="block text-white/70 text-sm mb-3">Imagen Principal</label>
            {form.cover_image ? (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-white/5">
                <img src={form.cover_image} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => setForm(prev => ({ ...prev, cover_image: '' }))}
                  className="absolute top-3 right-3 p-2 rounded-full bg-red-500 text-white"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="block aspect-video rounded-lg border-2 border-dashed border-white/20 hover:border-white/40 flex flex-col items-center justify-center cursor-pointer transition-colors">
                <Image size={48} className="text-white/20 mb-3" />
                <span className="text-white/40">Click para subir imagen</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploading} />
              </label>
            )}
            {uploading && <p className="text-[#D4AF37] text-sm mt-2">Subiendo...</p>}
          </div>
          
          {/* Name & Description */}
          <div className="glass-dark rounded-xl p-6 border border-white/5 space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Nombre (Español) *</label>
              <Input
                value={form.name.es}
                onChange={(e) => setForm(prev => ({ ...prev, name: { ...prev.name, es: e.target.value } }))}
                className="input-premium"
                placeholder="Nombre en español..."
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Nombre (English)</label>
              <Input
                value={form.name.en}
                onChange={(e) => setForm(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                className="input-premium"
                placeholder="Name in English..."
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">{type === 'venues' ? 'Descripción' : 'Biografía'} (Español)</label>
              <Textarea
                value={type === 'venues' ? form.description.es : form.biography.es}
                onChange={(e) => {
                  const field = type === 'venues' ? 'description' : 'biography';
                  setForm(prev => ({ ...prev, [field]: { ...prev[field], es: e.target.value } }));
                }}
                className="input-premium"
                rows={4}
                placeholder={type === 'venues' ? 'Descripción del venue...' : 'Biografía...'}
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">{type === 'venues' ? 'Descripción' : 'Biografía'} (English)</label>
              <Textarea
                value={type === 'venues' ? form.description.en : form.biography.en}
                onChange={(e) => {
                  const field = type === 'venues' ? 'description' : 'biography';
                  setForm(prev => ({ ...prev, [field]: { ...prev[field], en: e.target.value } }));
                }}
                className="input-premium"
                rows={4}
                placeholder={type === 'venues' ? 'Venue description...' : 'Biography...'}
              />
            </div>
          </div>
          
          {/* Gallery */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <MediaGalleryEditor
              images={form.gallery}
              videos={form.videos}
              onChange={({ images, videos }) => setForm(prev => ({ ...prev, gallery: images, videos }))}
              token={token}
            />
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <label className="block text-white/70 text-sm mb-3">Estado</label>
            <select
              value={form.status}
              onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
              className="input-premium w-full"
            >
              <option value="draft">Borrador</option>
              <option value="pending">Pendiente</option>
              <option value="approved">Publicado</option>
            </select>
            
            <div className="mt-4 pt-4 border-t border-white/10">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm(prev => ({ ...prev, featured: e.target.checked }))}
                  className="w-5 h-5 rounded bg-white/10 border-white/20"
                />
                <span className="text-white/80">Destacar en Homepage</span>
              </label>
            </div>
          </div>
          
          {/* Category / Type */}
          <div className="glass-dark rounded-xl p-6 border border-white/5 space-y-4">
            {type === 'venues' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Tipo de Venue</label>
                <select
                  value={form.venue_type}
                  onChange={(e) => setForm(prev => ({ ...prev, venue_type: e.target.value }))}
                  className="input-premium w-full"
                >
                  <option value="">Seleccionar...</option>
                  {categories.venues.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            )}
            
            {type !== 'venues' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Categorías</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {categories[type]?.map(cat => (
                    <label key={cat} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.categories.includes(cat)}
                        onChange={(e) => {
                          const newCats = e.target.checked
                            ? [...form.categories, cat]
                            : form.categories.filter(c => c !== cat);
                          setForm(prev => ({ ...prev, categories: newCats }));
                        }}
                        className="w-4 h-4 rounded bg-white/10 border-white/20"
                      />
                      <span className="text-white/70 text-sm">{cat}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {type === 'artists' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Géneros</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {genres.map(genre => (
                    <label key={genre} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.genres?.includes(genre)}
                        onChange={(e) => {
                          const newGenres = e.target.checked
                            ? [...(form.genres || []), genre]
                            : (form.genres || []).filter(g => g !== genre);
                          setForm(prev => ({ ...prev, genres: newGenres }));
                        }}
                        className="w-4 h-4 rounded bg-white/10 border-white/20"
                      />
                      <span className="text-white/70 text-sm">{genre}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Location */}
          <div className="glass-dark rounded-xl p-6 border border-white/5 space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Ciudad</label>
              <Input
                value={form.city}
                onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                className="input-premium"
                placeholder="Ciudad..."
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">País</label>
              <Input
                value={form.country}
                onChange={(e) => setForm(prev => ({ ...prev, country: e.target.value }))}
                className="input-premium"
                placeholder="País..."
              />
            </div>
            {type === 'venues' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Capacidad</label>
                <Input
                  type="number"
                  value={form.capacity}
                  onChange={(e) => setForm(prev => ({ ...prev, capacity: parseInt(e.target.value) || 0 }))}
                  className="input-premium"
                  placeholder="Ej: 500"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Projects Tab with Create/Edit
const ProjectsTab = ({ token, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [token]);

  const handleDelete = async (projectId) => {
    if (!window.confirm('¿Eliminar este proyecto permanentemente?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proyecto eliminado');
      fetchItems();
    } catch (error) {
      toast.error('Error eliminando proyecto');
    }
  };

  const filteredItems = items.filter(item => {
    const matchesFilter = filter === 'all' || item.status === filter;
    const name = item.title?.es || item.title?.en || '';
    const matchesSearch = name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) return <div className="text-white/50">Cargando...</div>;

  if (creating) {
    return (
      <ProjectEditor
        isNew={true}
        token={token}
        onSave={() => { setCreating(false); fetchItems(); }}
        onCancel={() => setCreating(false)}
      />
    );
  }

  if (editing) {
    return (
      <ProjectEditor
        project={editing}
        token={token}
        onSave={() => { setEditing(null); fetchItems(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Proyectos</h1>
          <p className="text-white/50">{items.length} proyectos totales</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-gold flex items-center gap-2">
          <Plus size={18} /> Nuevo Proyecto
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={20} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar proyectos..."
            className="input-premium pl-10"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-premium w-48"
        >
          <option value="all">Todos los estados</option>
          <option value="draft">Borrador</option>
          <option value="pending">Pendiente</option>
          <option value="approved">Publicado</option>
          <option value="hidden">Oculto</option>
          <option value="archived">Archivado</option>
        </select>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map((item) => (
          <div key={item.project_id} className="glass-dark rounded-xl overflow-hidden border border-white/5 hover:border-white/10 transition-colors">
            <div className="aspect-video relative overflow-hidden bg-white/5">
              {item.cover_image ? (
                <img src={item.cover_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FolderOpen size={32} className="text-white/20" />
                </div>
              )}
              <div className="absolute top-3 left-3">
                <StatusBadge status={item.status} />
              </div>
              {item.featured && (
                <div className="absolute top-3 right-3 px-2 py-1 rounded bg-[#D4AF37] text-black text-xs font-bold">
                  ⭐ DESTACADO
                </div>
              )}
            </div>

            <div className="p-4">
              <h3 className="text-white font-medium mb-1 truncate">{item.title?.es || item.title?.en || 'Sin título'}</h3>
              <p className="text-white/50 text-sm mb-4">{item.category || item.city || ''}</p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditing(item)}
                  className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center gap-2"
                >
                  <Edit size={14} /> Editar
                </button>
                <button
                  onClick={() => handleDelete(item.project_id)}
                  className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <FolderOpen size={48} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/50">No se encontraron proyectos</p>
          <button onClick={() => setCreating(true)} className="btn-gold mt-4">
            Crear Primer Proyecto
          </button>
        </div>
      )}
    </div>
  );
};

// Content Management Tab (Artists, Venues, Models)
const ContentManagementTab = ({ type, token, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [toggling, setToggling] = useState({});

  const typeConfig = {
    artists: { label: 'Artistas', singular: 'Artista', icon: Music, idField: 'artist_id', nameField: 'name', descField: 'biography' },
    venues: { label: 'Venues', singular: 'Venue', icon: Building2, idField: 'venue_id', nameField: 'name', descField: 'description' },
    models: { label: 'Modelos', singular: 'Modelo', icon: Camera, idField: 'model_id', nameField: 'name', descField: 'biography' },
  };

  const config = typeConfig[type];

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data.items || []);
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    setLoading(true);
    fetchItems(); 
  }, [type, token]);

  const toggleFeatured = async (itemId, currentFeatured) => {
    setToggling(prev => ({ ...prev, [itemId]: true }));
    try {
      await axios.post(`${API}/admin/${type}/${itemId}/toggle-featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentFeatured ? 'Quitado de destacados' : 'Agregado a destacados');
      fetchItems();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setToggling(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const reviewContent = async (itemId, status) => {
    try {
      const singularType = type.slice(0, -1);
      await axios.post(`${API}/admin/review/${singularType}/${itemId}`, {
        status,
        notes: reviewNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(status === 'approved' ? 'Contenido aprobado' : status === 'rejected' ? 'Contenido rechazado' : 'Cambios solicitados');
      setReviewModal(null);
      setReviewNotes('');
      fetchItems();
    } catch (error) {
      toast.error('Error al revisar contenido');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm(`¿Eliminar este ${config.label.slice(0, -1).toLowerCase()} permanentemente?`)) return;
    try {
      await axios.delete(`${API}/admin/${type}/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eliminado correctamente');
      fetchItems();
    } catch (error) {
      toast.error('Error eliminando');
    }
  };

  const filteredItems = items.filter(item => {
    const matchesFilter = filter === 'all' || item.status === filter;
    const name = item[config.nameField]?.es || item[config.nameField]?.en || '';
    const matchesSearch = name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) return <div className="text-white/50">Cargando...</div>;

  if (creating) {
    return (
      <ContentCreator
        type={type}
        config={config}
        token={token}
        onSave={() => { setCreating(false); fetchItems(); }}
        onCancel={() => setCreating(false)}
      />
    );
  }

  if (editing) {
    return (
      <ContentEditor
        type={type}
        item={editing}
        token={token}
        onSave={() => { setEditing(null); fetchItems(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">{config.label}</h1>
          <p className="text-white/50">{items.length} registros totales</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-gold flex items-center gap-2">
          <Plus size={18} /> Nuevo {config.singular}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={20} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="input-premium pl-10"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-premium w-48"
        >
          <option value="all">Todos los estados</option>
          <option value="draft">Borrador</option>
          <option value="pending">Pendiente</option>
          <option value="approved">Aprobado</option>
          <option value="rejected">Rechazado</option>
          <option value="changes_requested">Cambios</option>
        </select>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map((item) => {
          const itemId = item[config.idField];
          const name = item[config.nameField]?.es || item[config.nameField]?.en || 'Sin nombre';
          
          return (
            <div key={itemId} className="glass-dark rounded-xl overflow-hidden border border-white/5 hover:border-white/10 transition-colors">
              <div className="aspect-video relative overflow-hidden bg-white/5">
                {item.cover_image ? (
                  <img src={item.cover_image} alt={name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <config.icon size={32} className="text-white/20" />
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <StatusBadge status={item.status} />
                </div>
                {item.featured && (
                  <div className="absolute top-3 right-3 px-2 py-1 rounded bg-[#D4AF37] text-black text-xs font-bold">
                    ⭐
                  </div>
                )}
              </div>

              <div className="p-4">
                <h3 className="text-white font-medium mb-1 truncate">{name}</h3>
                <p className="text-white/50 text-sm mb-4">{item.city || item.category || ''}</p>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditing(item)}
                    className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center gap-2"
                  >
                    <Edit size={14} /> Editar
                  </button>
                  {item.status === 'pending' && (
                    <button
                      onClick={() => setReviewModal(item)}
                      className="flex-1 btn-gold text-sm py-2"
                    >
                      Revisar
                    </button>
                  )}
                  {item.status === 'approved' && (
                    <FeaturedToggle
                      featured={item.featured}
                      loading={toggling[itemId]}
                      onToggle={() => toggleFeatured(itemId, item.featured)}
                    />
                  )}
                  <button
                    onClick={() => handleDelete(itemId)}
                    className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12 text-white/50">
          No se encontraron {config.label.toLowerCase()} con estos filtros
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setReviewModal(null)}>
          <div className="glass-dark rounded-2xl p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl text-white mb-2">Revisar Contenido</h3>
            <p className="text-white/50 mb-6">
              {reviewModal[config.nameField]?.es || reviewModal[config.nameField]?.en}
            </p>
            
            <Textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Comentarios para el usuario..."
              className="input-premium mb-6"
              rows={4}
            />
            
            <div className="flex gap-3">
              <button
                onClick={() => reviewContent(reviewModal[config.idField], 'approved')}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Check size={18} /> Aprobar
              </button>
              <button
                onClick={() => reviewContent(reviewModal[config.idField], 'changes_requested')}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black py-3 rounded-lg font-medium transition-colors"
              >
                Cambios
              </button>
              <button
                onClick={() => reviewContent(reviewModal[config.idField], 'rejected')}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <X size={18} /> Rechazar
              </button>
            </div>
            
            <button onClick={() => setReviewModal(null)} className="w-full mt-4 btn-secondary">
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Messages Tab (Internal Messaging)
const MessagesTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [form, setForm] = useState({
    type: 'anuncio',
    subject: '',
    content: '',
    audience: 'all',
    target_role: '',
    target_users: []
  });
  
  const messageTypes = [
    { value: 'bienvenida', label: 'Bienvenida' },
    { value: 'anuncio', label: 'Anuncio Importante' },
    { value: 'actualizacion', label: 'Actualización de Plataforma' },
    { value: 'cambio_estado', label: 'Cambio de Estado' },
    { value: 'recordatorio', label: 'Recordatorio' },
    { value: 'operativo', label: 'Aviso Operativo' },
    { value: 'correccion', label: 'Solicitud de Corrección' },
    { value: 'promocion', label: 'Promoción / Convocatoria' }
  ];
  
  const roles = [
    { value: 'artist', label: 'Artistas' },
    { value: 'model', label: 'Modelos' },
    { value: 'venue_owner', label: 'Venue Owners' },
    { value: 'curator', label: 'Curadores' },
    { value: 'admin', label: 'Administradores' }
  ];
  
  const fetchData = async () => {
    try {
      const [usersRes, messagesRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data.items || []);
      setSentMessages(messagesRes.data.items || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchData(); }, [token]);
  
  const handleSend = async () => {
    if (!form.subject.trim() || !form.content.trim()) {
      toast.error('Completa asunto y contenido');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/messages/send`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Mensaje enviado a ${response.data.recipients_count} usuarios`);
      setForm({ type: 'anuncio', subject: '', content: '', audience: 'all', target_role: '', target_users: [] });
      fetchData();
    } catch (error) {
      toast.error('Error enviando mensaje');
    } finally {
      setSending(false);
    }
  };
  
  if (loading) return <div className="text-white/50">Cargando...</div>;
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-white mb-2">Mensajería Interna</h1>
        <p className="text-white/50">Envía notificaciones dentro de la plataforma</p>
      </div>
      
      <div className="grid grid-cols-3 gap-6">
        {/* Compose */}
        <div className="col-span-2 glass-dark rounded-xl p-6 border border-white/5">
          <h2 className="text-white font-display text-xl mb-6 flex items-center gap-2">
            <Send size={20} className="text-[#D4AF37]" /> Nuevo Mensaje
          </h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Tipo de Mensaje</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                  className="input-premium w-full"
                >
                  {messageTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-2">Audiencia</label>
                <select
                  value={form.audience}
                  onChange={(e) => setForm(prev => ({ ...prev, audience: e.target.value }))}
                  className="input-premium w-full"
                >
                  <option value="all">Todos los usuarios</option>
                  <option value="role">Por rol</option>
                  <option value="selected">Usuarios específicos</option>
                </select>
              </div>
            </div>
            
            {form.audience === 'role' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Seleccionar Rol</label>
                <select
                  value={form.target_role}
                  onChange={(e) => setForm(prev => ({ ...prev, target_role: e.target.value }))}
                  className="input-premium w-full"
                >
                  <option value="">Seleccionar...</option>
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
            )}
            
            {form.audience === 'selected' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Seleccionar Usuarios</label>
                <div className="max-h-40 overflow-y-auto space-y-1 p-3 rounded-lg bg-white/5">
                  {users.map(user => (
                    <label key={user.user_id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-white/5 rounded">
                      <input
                        type="checkbox"
                        checked={form.target_users.includes(user.user_id)}
                        onChange={(e) => {
                          const newUsers = e.target.checked
                            ? [...form.target_users, user.user_id]
                            : form.target_users.filter(id => id !== user.user_id);
                          setForm(prev => ({ ...prev, target_users: newUsers }));
                        }}
                        className="w-4 h-4 rounded bg-white/10"
                      />
                      <span className="text-white/80 text-sm">{user.name}</span>
                      <span className="text-white/40 text-xs">({user.role})</span>
                    </label>
                  ))}
                </div>
                <p className="text-white/40 text-xs mt-1">{form.target_users.length} seleccionados</p>
              </div>
            )}
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Asunto</label>
              <Input
                value={form.subject}
                onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
                className="input-premium"
                placeholder="Título del mensaje..."
              />
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Contenido</label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                className="input-premium"
                rows={6}
                placeholder="Escribe el mensaje..."
              />
            </div>
            
            <button
              onClick={handleSend}
              disabled={sending || !form.subject || !form.content}
              className="btn-gold w-full flex items-center justify-center gap-2"
            >
              {sending ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
              {sending ? 'Enviando...' : 'Enviar Mensaje'}
            </button>
          </div>
        </div>
        
        {/* History */}
        <div className="glass-dark rounded-xl p-6 border border-white/5">
          <h2 className="text-white font-display text-lg mb-4">Mensajes Enviados</h2>
          
          {sentMessages.length === 0 ? (
            <p className="text-white/40 text-sm">No hay mensajes enviados</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {sentMessages.map((msg) => (
                <div key={msg.message_id} className="p-3 rounded-lg bg-white/5">
                  <p className="text-white font-medium text-sm truncate">{msg.subject}</p>
                  <p className="text-white/50 text-xs mt-1">
                    {msg.recipients?.length || 0} destinatarios • {new Date(msg.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Emails Tab
const EmailsTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [form, setForm] = useState({
    type: 'anuncio',
    subject: '',
    content: '',
    audience: 'all',
    target_role: '',
    target_users: [],
    cta_text: '',
    cta_url: ''
  });
  
  const emailTypes = [
    { value: 'bienvenida', label: 'Bienvenida', emoji: '👋' },
    { value: 'anuncio', label: 'Anuncio Importante', emoji: '📢' },
    { value: 'actualizacion', label: 'Actualización', emoji: '🔄' },
    { value: 'aviso', label: 'Aviso', emoji: '⚠️' },
    { value: 'promocion', label: 'Promoción', emoji: '🎉' },
    { value: 'recordatorio', label: 'Recordatorio', emoji: '⏰' },
    { value: 'operativo', label: 'Aviso Operativo', emoji: '⚙️' }
  ];
  
  const roles = [
    { value: 'artist', label: 'Artistas' },
    { value: 'model', label: 'Modelos' },
    { value: 'venue_owner', label: 'Venue Owners' },
    { value: 'curator', label: 'Curadores' },
    { value: 'admin', label: 'Administradores' }
  ];
  
  const fetchData = async () => {
    try {
      const [usersRes, emailsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/emails`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data.items || []);
      setEmailHistory(emailsRes.data.items || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchData(); }, [token]);
  
  const handleSend = async () => {
    if (!form.subject.trim() || !form.content.trim()) {
      toast.error('Completa asunto y contenido');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/emails/send`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.note) {
        toast.warning(response.data.note);
      } else {
        toast.success(`Email enviado a ${response.data.sent} usuarios`);
      }
      
      setForm({ type: 'anuncio', subject: '', content: '', audience: 'all', target_role: '', target_users: [], cta_text: '', cta_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Error enviando email');
    } finally {
      setSending(false);
    }
  };
  
  const sendWelcome = async (userId) => {
    try {
      await axios.post(`${API}/admin/emails/welcome/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email de bienvenida enviado');
    } catch (error) {
      toast.error('Error enviando email');
    }
  };
  
  if (loading) return <div className="text-white/50">Cargando...</div>;
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-white mb-2">Envío de Emails</h1>
        <p className="text-white/50">Envía correos electrónicos a usuarios de la plataforma</p>
      </div>
      
      <div className="grid grid-cols-3 gap-6">
        {/* Compose */}
        <div className="col-span-2 glass-dark rounded-xl p-6 border border-white/5">
          <h2 className="text-white font-display text-xl mb-6 flex items-center gap-2">
            <Mail size={20} className="text-[#D4AF37]" /> Nuevo Email
          </h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Tipo de Email</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                  className="input-premium w-full"
                >
                  {emailTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.emoji} {type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-2">Audiencia</label>
                <select
                  value={form.audience}
                  onChange={(e) => setForm(prev => ({ ...prev, audience: e.target.value }))}
                  className="input-premium w-full"
                >
                  <option value="all">Todos los usuarios</option>
                  <option value="role">Por rol</option>
                  <option value="selected">Usuarios específicos</option>
                </select>
              </div>
            </div>
            
            {form.audience === 'role' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Seleccionar Rol</label>
                <select
                  value={form.target_role}
                  onChange={(e) => setForm(prev => ({ ...prev, target_role: e.target.value }))}
                  className="input-premium w-full"
                >
                  <option value="">Seleccionar...</option>
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
            )}
            
            {form.audience === 'selected' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Seleccionar Usuarios</label>
                <div className="max-h-40 overflow-y-auto space-y-1 p-3 rounded-lg bg-white/5">
                  {users.map(user => (
                    <label key={user.user_id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-white/5 rounded">
                      <input
                        type="checkbox"
                        checked={form.target_users.includes(user.user_id)}
                        onChange={(e) => {
                          const newUsers = e.target.checked
                            ? [...form.target_users, user.user_id]
                            : form.target_users.filter(id => id !== user.user_id);
                          setForm(prev => ({ ...prev, target_users: newUsers }));
                        }}
                        className="w-4 h-4 rounded bg-white/10"
                      />
                      <span className="text-white/80 text-sm">{user.name}</span>
                      <span className="text-white/40 text-xs">{user.email}</span>
                    </label>
                  ))}
                </div>
                <p className="text-white/40 text-xs mt-1">{form.target_users.length} seleccionados</p>
              </div>
            )}
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Asunto</label>
              <Input
                value={form.subject}
                onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
                className="input-premium"
                placeholder="Asunto del email..."
              />
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Contenido</label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                className="input-premium"
                rows={8}
                placeholder="Escribe el contenido del email..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Texto del Botón (opcional)</label>
                <Input
                  value={form.cta_text}
                  onChange={(e) => setForm(prev => ({ ...prev, cta_text: e.target.value }))}
                  className="input-premium"
                  placeholder="Ej: Ver más..."
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">URL del Botón (opcional)</label>
                <Input
                  value={form.cta_url}
                  onChange={(e) => setForm(prev => ({ ...prev, cta_url: e.target.value }))}
                  className="input-premium"
                  placeholder="https://..."
                />
              </div>
            </div>
            
            <button
              onClick={handleSend}
              disabled={sending || !form.subject || !form.content}
              className="btn-gold w-full flex items-center justify-center gap-2"
            >
              {sending ? <RefreshCw size={18} className="animate-spin" /> : <Mail size={18} />}
              {sending ? 'Enviando...' : 'Enviar Email'}
            </button>
          </div>
        </div>
        
        {/* History & Quick Actions */}
        <div className="space-y-6">
          {/* Quick Welcome Emails */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h3 className="text-white font-display text-lg mb-4">Email de Bienvenida</h3>
            <p className="text-white/50 text-sm mb-4">Envía un email de bienvenida a usuarios nuevos</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {users.slice(0, 10).map(user => (
                <div key={user.user_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-white/5">
                  <span className="text-white/70 text-sm truncate flex-1">{user.name}</span>
                  <button
                    onClick={() => sendWelcome(user.user_id)}
                    className="text-[#D4AF37] text-xs hover:underline"
                  >
                    Enviar
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          {/* History */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h3 className="text-white font-display text-lg mb-4">Historial</h3>
            
            {emailHistory.length === 0 ? (
              <p className="text-white/40 text-sm">No hay emails enviados</p>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {emailHistory.map((email) => (
                  <div key={email.email_id} className="p-3 rounded-lg bg-white/5">
                    <p className="text-white font-medium text-sm truncate">{email.subject}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs ${email.status === 'sent' ? 'text-green-400' : 'text-yellow-400'}`}>
                        {email.status === 'sent' ? '✓ Enviado' : '⏳ Enviando'}
                      </span>
                      <span className="text-white/40 text-xs">
                        • {email.recipients_count} destinatarios
                      </span>
                    </div>
                    <p className="text-white/40 text-xs mt-1">
                      {new Date(email.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Homepage Control Tab (existing, keep as is)
const HomepageControlTab = ({ token, onRefresh }) => {
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [featuredVenues, setFeaturedVenues] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [allArtists, setAllArtists] = useState([]);
  const [allVenues, setAllVenues] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});

  const fetchData = async () => {
    try {
      const [artistsRes, venuesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/admin/artists`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/venues`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/projects`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const artists = artistsRes.data.items || [];
      const venues = venuesRes.data.items || [];
      const projects = projectsRes.data.items || [];
      
      setAllArtists(artists.filter(a => a.status === 'approved'));
      setAllVenues(venues.filter(v => v.status === 'approved'));
      setAllProjects(projects.filter(p => p.status === 'approved'));
      
      setFeaturedArtists(artists.filter(a => a.featured && a.status === 'approved'));
      setFeaturedVenues(venues.filter(v => v.featured && v.status === 'approved'));
      setFeaturedProjects(projects.filter(p => p.featured && p.status === 'approved'));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token]);

  const toggleFeatured = async (type, id, currentFeatured) => {
    const key = `${type}-${id}`;
    setToggling(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API}/admin/${type}/${id}/toggle-featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentFeatured ? 'Quitado de destacados' : 'Agregado a destacados');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setToggling(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) return <div className="text-white/50">Cargando...</div>;

  const ContentGrid = ({ title, items, type, featured }) => (
    <div className="glass-dark rounded-xl p-6 border border-white/5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-display text-lg">{title}</h3>
        <span className="text-[#D4AF37] text-sm">{featured.length} destacados</span>
      </div>
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {items.map((item) => {
          const name = item.name?.es || item.title?.es || 'Sin nombre';
          const isFeatured = item.featured;
          const itemId = item[`${type.slice(0, -1)}_id`] || item.project_id;
          const key = `${type}-${itemId}`;
          
          return (
            <div key={itemId} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
              <div className="flex items-center gap-3">
                {item.cover_image && (
                  <img src={item.cover_image} alt={name} className="w-10 h-10 rounded object-cover" />
                )}
                <span className="text-white text-sm">{name}</span>
              </div>
              <FeaturedToggle
                featured={isFeatured}
                loading={toggling[key]}
                onToggle={() => toggleFeatured(type, itemId, isFeatured)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Control de Homepage</h1>
          <p className="text-white/50">Gestiona el contenido destacado en la página principal</p>
        </div>
        <button onClick={fetchData} className="btn-secondary flex items-center gap-2">
          <RefreshCw size={18} /> Actualizar
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ContentGrid title="Artistas" items={allArtists} type="artists" featured={featuredArtists} />
        <ContentGrid title="Venues" items={allVenues} type="venues" featured={featuredVenues} />
        <ContentGrid title="Proyectos" items={allProjects} type="projects" featured={featuredProjects} />
      </div>
    </div>
  );
};

// Pending Reviews Tab
const PendingReviewsTab = ({ token, onNavigate }) => {
  const [pending, setPending] = useState({ artists: [], venues: [], projects: [], models: [] });
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchPending = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPending(response.data);
    } catch (error) {
      console.error('Error fetching pending:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, [token]);

  const reviewContent = async (type, itemId, status) => {
    try {
      await axios.post(`${API}/admin/review/${type}/${itemId}`, {
        status,
        notes: reviewNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(status === 'approved' ? 'Contenido aprobado' : status === 'rejected' ? 'Contenido rechazado' : 'Cambios solicitados');
      setReviewModal(null);
      setReviewNotes('');
      fetchPending();
    } catch (error) {
      toast.error('Error al revisar contenido');
    }
  };

  const totalPending = pending.artists.length + pending.venues.length + pending.projects.length + (pending.models?.length || 0);

  if (loading) return <div className="text-white/50">Cargando...</div>;

  const PendingSection = ({ title, items, type, idField, nameField, icon: Icon }) => {
    if (items.length === 0) return null;
    
    return (
      <div className="glass-dark rounded-xl p-6 border border-white/5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
            <Icon size={20} className="text-yellow-400" />
          </div>
          <div>
            <h3 className="text-white font-medium">{title}</h3>
            <p className="text-white/50 text-sm">{items.length} pendientes</p>
          </div>
        </div>
        
        <div className="space-y-2">
          {items.map((item) => {
            const name = item[nameField]?.es || item[nameField]?.en || 'Sin nombre';
            return (
              <div key={item[idField]} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div className="flex items-center gap-3">
                  {item.cover_image && (
                    <img src={item.cover_image} alt={name} className="w-12 h-12 rounded object-cover" />
                  )}
                  <div>
                    <p className="text-white text-sm">{name}</p>
                    <p className="text-white/40 text-xs">{item.city || item.category || ''}</p>
                  </div>
                </div>
                <button
                  onClick={() => setReviewModal({ ...item, type: type.slice(0, -1), idField })}
                  className="btn-gold text-sm py-2 px-4"
                >
                  Revisar
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-white mb-2">Revisiones Pendientes</h1>
        <p className="text-white/50">{totalPending} elementos esperando aprobación</p>
      </div>

      {totalPending === 0 ? (
        <div className="glass-dark rounded-xl p-12 text-center border border-white/5">
          <Check size={48} className="text-green-400 mx-auto mb-4" />
          <h3 className="text-white font-display text-xl mb-2">¡Todo al día!</h3>
          <p className="text-white/50">No hay contenido pendiente de revisión</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PendingSection title="Artistas" items={pending.artists} type="artists" idField="artist_id" nameField="name" icon={Music} />
          <PendingSection title="Venues" items={pending.venues} type="venues" idField="venue_id" nameField="name" icon={Building2} />
          <PendingSection title="Proyectos" items={pending.projects} type="projects" idField="project_id" nameField="title" icon={FolderOpen} />
          <PendingSection title="Modelos" items={pending.models || []} type="models" idField="model_id" nameField="name" icon={Camera} />
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setReviewModal(null)}>
          <div className="glass-dark rounded-2xl p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl text-white mb-6">Revisar Contenido</h3>
            
            <Textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Comentarios para el usuario..."
              className="input-premium mb-6"
              rows={4}
            />
            
            <div className="flex gap-3">
              <button
                onClick={() => reviewContent(reviewModal.type, reviewModal[reviewModal.idField], 'approved')}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Check size={18} /> Aprobar
              </button>
              <button
                onClick={() => reviewContent(reviewModal.type, reviewModal[reviewModal.idField], 'changes_requested')}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black py-3 rounded-lg font-medium transition-colors"
              >
                Cambios
              </button>
              <button
                onClick={() => reviewContent(reviewModal.type, reviewModal[reviewModal.idField], 'rejected')}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <X size={18} /> Rechazar
              </button>
            </div>
            
            <button onClick={() => setReviewModal(null)} className="w-full mt-4 btn-secondary">
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Users Tab
const UsersTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.items || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [token]);

  const updateUserRole = async (userId, newRole) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rol actualizado');
      fetchUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Error actualizando rol');
    } finally {
      setUpdating(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_active: !currentStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? 'Usuario desactivado' : 'Usuario activado');
      fetchUsers();
    } catch (error) {
      toast.error('Error actualizando estado');
    }
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'all') return true;
    return user.role === filter;
  });

  const roleColors = {
    super_admin: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Super Admin' },
    admin: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Admin' },
    curator: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: 'Curator' },
    artist: { bg: 'bg-[#D4AF37]/20', text: 'text-[#D4AF37]', label: 'Artista' },
    model: { bg: 'bg-pink-500/20', text: 'text-pink-400', label: 'Modelo' },
    venue_owner: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Venue Owner' },
  };

  if (loading) return <div className="text-white/50">Cargando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Gestión de Usuarios</h1>
          <p className="text-white/50">{users.length} usuarios registrados</p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-premium w-48"
        >
          <option value="all">Todos los roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="curator">Curator</option>
          <option value="artist">Artista</option>
          <option value="model">Modelo</option>
          <option value="venue_owner">Venue Owner</option>
        </select>
      </div>

      <div className="glass-dark rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Usuario</th>
              <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Email</th>
              <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Rol</th>
              <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Estado</th>
              <th className="text-right text-white/50 text-xs uppercase tracking-wider p-4">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const roleInfo = roleColors[user.role] || roleColors.artist;
              const isActive = user.is_active !== false;
              
              return (
                <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                          <Users size={18} className="text-white/40" />
                        </div>
                      )}
                      <span className="text-white font-medium">{user.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-white/60">{user.email}</td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${roleInfo.bg} ${roleInfo.text}`}>
                      {roleInfo.label}
                    </span>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => toggleUserStatus(user.user_id, isActive)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {isActive ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => setSelectedUser(user)}
                      className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setSelectedUser(null)}>
          <div className="glass-dark rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl text-white mb-2">Editar Usuario</h3>
            <p className="text-white/50 mb-6">{selectedUser.email}</p>
            
            <h4 className="text-white/70 text-sm mb-3">Cambiar Rol</h4>
            <div className="space-y-2">
              {Object.entries(roleColors).map(([roleKey, roleInfo]) => (
                <button
                  key={roleKey}
                  onClick={() => updateUserRole(selectedUser.user_id, roleKey)}
                  disabled={updating || selectedUser.role === roleKey}
                  className={`w-full p-4 rounded-lg text-left transition-colors flex items-center justify-between ${
                    selectedUser.role === roleKey 
                      ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/30' 
                      : 'bg-white/5 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <span className={roleInfo.text}>{roleInfo.label}</span>
                  {selectedUser.role === roleKey && <Check size={16} className="text-[#D4AF37]" />}
                </button>
              ))}
            </div>
            
            <button onClick={() => setSelectedUser(null)} className="w-full mt-6 btn-secondary">
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Leads Tab
const LeadsTab = ({ token }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [internalNotes, setInternalNotes] = useState('');

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data.items || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, [token]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estado actualizado');
      fetchLeads();
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const addNote = async (leadId) => {
    if (!internalNotes.trim()) return;
    try {
      await axios.post(`${API}/admin/leads/${leadId}/notes`, { note: internalNotes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Nota agregada');
      setInternalNotes('');
      fetchLeads();
    } catch (error) {
      toast.error('Error al agregar nota');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const statusMatch = filter === 'all' || lead.status === filter;
    const typeMatch = typeFilter === 'all' || lead.form_type === typeFilter;
    return statusMatch && typeMatch;
  });

  const uniqueTypes = [...new Set(leads.map(l => l.form_type).filter(Boolean))];

  if (loading) return <div className="text-white/50">Cargando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Leads & Contactos</h1>
          <p className="text-white/50">{leads.length} leads totales</p>
        </div>
        <div className="flex gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-premium text-sm py-2 px-3">
            <option value="all">Todos los estados</option>
            <option value="new">Nuevo</option>
            <option value="contacted">Contactado</option>
            <option value="in_progress">En Proceso</option>
            <option value="completed">Cerrado</option>
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="input-premium text-sm py-2 px-3">
            <option value="all">Todos los tipos</option>
            {uniqueTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>
      
      {filteredLeads.length === 0 ? (
        <div className="glass-dark rounded-xl p-12 text-center">
          <MessageSquare size={48} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/50">No hay leads con estos filtros</p>
        </div>
      ) : (
        <div className="glass-dark rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Contacto</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Tipo</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Fecha</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Estado</th>
                <th className="text-right text-white/50 text-xs uppercase tracking-wider p-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr key={lead.lead_id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setSelectedLead(lead)}>
                  <td className="p-4">
                    <p className="text-white font-medium">{lead.full_name}</p>
                    <p className="text-white/50 text-sm">{lead.email}</p>
                  </td>
                  <td className="p-4 text-white/70">{lead.form_type || 'contact'}</td>
                  <td className="p-4 text-white/50 text-sm">{new Date(lead.created_at).toLocaleDateString()}</td>
                  <td className="p-4"><StatusBadge status={lead.status || 'new'} /></td>
                  <td className="p-4 text-right">
                    <button className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors">
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelectedLead(null)}>
          <div className="glass-dark rounded-2xl p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-display text-2xl text-white">{selectedLead.full_name}</h3>
                <p className="text-white/50 text-sm mt-1">{selectedLead.form_type} • {new Date(selectedLead.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => setSelectedLead(null)} className="text-white/60 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            {/* Status controls */}
            <div className="flex gap-2 mb-6">
              {['new', 'contacted', 'in_progress', 'completed'].map(status => (
                <button
                  key={status}
                  onClick={() => updateLeadStatus(selectedLead.lead_id, status)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                    selectedLead.status === status 
                      ? status === 'new' ? 'bg-blue-500 text-white' :
                        status === 'contacted' ? 'bg-yellow-500 text-black' :
                        status === 'in_progress' ? 'bg-purple-500 text-white' :
                        'bg-green-500 text-black'
                      : 'bg-white/10 text-white/60 hover:bg-white/20'
                  }`}
                >
                  {status === 'new' ? 'Nuevo' : status === 'contacted' ? 'Contactado' : status === 'in_progress' ? 'En Proceso' : 'Cerrado'}
                </button>
              ))}
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4 text-sm">
                <h4 className="text-white/50 uppercase tracking-wider text-xs mb-3">Contacto</h4>
                <div><span className="text-white/50">Email:</span> <a href={`mailto:${selectedLead.email}`} className="text-[#D4AF37] ml-2">{selectedLead.email}</a></div>
                {selectedLead.phone && <div><span className="text-white/50">Teléfono:</span> <a href={`tel:${selectedLead.phone}`} className="text-[#D4AF37] ml-2">{selectedLead.phone}</a></div>}
                {selectedLead.company && <div><span className="text-white/50">Empresa:</span> <span className="text-white ml-2">{selectedLead.company}</span></div>}
              </div>
              
              <div className="space-y-4 text-sm">
                <h4 className="text-white/50 uppercase tracking-wider text-xs mb-3">Evento</h4>
                {selectedLead.requested_service && <div><span className="text-white/50">Servicio:</span> <span className="text-white ml-2">{selectedLead.requested_service}</span></div>}
                {selectedLead.event_type && <div><span className="text-white/50">Tipo:</span> <span className="text-white ml-2">{selectedLead.event_type}</span></div>}
                {selectedLead.event_date && <div><span className="text-white/50">Fecha:</span> <span className="text-white ml-2">{selectedLead.event_date}</span></div>}
              </div>
            </div>
            
            {selectedLead.message && (
              <div className="mt-6 pt-4 border-t border-white/10">
                <span className="text-white/50 block mb-2 text-sm">Mensaje:</span>
                <p className="text-white/80 whitespace-pre-line bg-white/5 p-4 rounded-lg">{selectedLead.message}</p>
              </div>
            )}
            
            {/* Internal Notes */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <h4 className="text-white/50 uppercase tracking-wider text-xs mb-3">Notas Internas</h4>
              
              {selectedLead.internal_notes?.length > 0 && (
                <div className="space-y-3 mb-4">
                  {selectedLead.internal_notes.map((note, idx) => (
                    <div key={idx} className="bg-white/5 p-3 rounded-lg">
                      <p className="text-white/80 text-sm">{note.note}</p>
                      <p className="text-white/40 text-xs mt-1">{note.author} • {new Date(note.timestamp).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <Textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Agregar nota interna..."
                  className="input-premium flex-1 text-sm"
                  rows={2}
                />
                <button onClick={() => addNote(selectedLead.lead_id)} disabled={!internalNotes.trim()} className="btn-gold px-4 self-end disabled:opacity-50">
                  Agregar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main SuperAdminDashboard Component
export default function SuperAdminDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || (user.role !== 'super_admin' && user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchStats();
  }, [token]);

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab stats={stats} loading={loading} onNavigate={setActiveTab} />;
      case 'homepage':
        return <HomepageControlTab token={token} onRefresh={fetchStats} />;
      case 'pending':
        return <PendingReviewsTab token={token} onNavigate={setActiveTab} />;
      case 'projects':
        return <ProjectsTab token={token} onRefresh={fetchStats} />;
      case 'artists':
        return <ContentManagementTab type="artists" token={token} onRefresh={fetchStats} />;
      case 'venues':
        return <ContentManagementTab type="venues" token={token} onRefresh={fetchStats} />;
      case 'transport':
      case 'events':
        return <EventsTab token={token} />;
      case 'payments':
        return <PaymentsTab token={token} />;
      case 'customers':
        return <CustomersTab token={token} />;
      case 'checkin':
        return <CheckInTab token={token} />;
      case 'invitations':
        return <InvitationsTab token={token} />;
      case 'analytics':
        return <AnalyticsTab token={token} />;
        return <CheckInTab token={token} />;
        return <TransportTab token={token} onRefresh={fetchStats} />;
      case 'models':
        return <ContentManagementTab type="models" token={token} onRefresh={fetchStats} />;
      case 'leads':
        return <LeadsTab token={token} />;
      case 'users':
        return <UsersTab token={token} />;
      case 'inbox':
        return <AdminInbox token={token} />;
      case 'messages':
        return <MessagesTab token={token} />;
      case 'emails':
        return <EmailsTab token={token} />;
      case 'settings':
        return (
          <div className="glass-dark rounded-xl p-12 text-center">
            <Settings size={48} className="text-white/20 mx-auto mb-4" />
            <h2 className="text-white font-display text-xl mb-2">Configuración</h2>
            <p className="text-white/50">Próximamente: configuración del sistema</p>
          </div>
        );
      default:
        return <OverviewTab stats={stats} loading={loading} onNavigate={setActiveTab} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} userRole={user?.role} />
      
      <main className="ml-64 p-8">
        {renderContent()}
      </main>
    </div>
  );
}
